import type { SnapshotRegenerationStatusResponse } from "@repo/shared/types";
import { useQueryClient } from "@tanstack/react-query";
import { getWithToken } from "@/lib/api/_client";
import { useSessionToken } from "@/lib/context/public-session-context";
import { flowInstanceQueryKeys } from "@/lib/hooks/use-flow-instances/use-flow-instances";
import { useSnapshotRegenerationWatch } from "@/lib/hooks/use-snapshot-regeneration-watch";
import {
  publicGoalSnapshotQueryKeys,
  useInvalidateAllPublicPortfolioSnapshots,
} from "./use-goal-snapshots";
import { useInvalidatePublicFlowCompletion } from "./use-public-flow-completion";

export const publicSnapshotRegenerationQueryKeys = {
  /** QL-675 — the rebuild status, polled while a rebuild runs (never cached). */
  status: (instanceId: string) =>
    ["publicFlow", "instance", instanceId, "snapshot-status"] as const,
  /** Every instance-scoped snapshot (cost, expected value, asset-class forecast, …). */
  allForInstance: (instanceId: string) =>
    ["publicFlow", "instance", instanceId, "snapshot"] as const,
};

/**
 * QL-675 — the public flow's side of `useSnapshotRegenerationWatch`: follow the
 * worker's rebuild of the session's instance and, when it ends, refresh what
 * it rewrote (D30 mirror) — every goal-, portfolio- and instance-scoped
 * snapshot view, the instance-wide product costs and the completion — else the widgets and the banner keep
 * serving the stale "missing" state. `onFinished` gets the final status, so the
 * caller can set its own copy (a flag still set means the rebuild failed).
 */
export function usePublicSnapshotRegenerationWatch(opts: {
  instanceId: string;
  goalIds: string[];
  regenerating: boolean;
  onFinished: (status: SnapshotRegenerationStatusResponse) => void;
}): { watching: boolean } {
  const { instanceId, goalIds, regenerating, onFinished } = opts;
  const sessionToken = useSessionToken();
  const queryClient = useQueryClient();
  const invalidateAllPortfolioSnapshots = useInvalidateAllPublicPortfolioSnapshots();
  const invalidateCompletion = useInvalidatePublicFlowCompletion();

  return useSnapshotRegenerationWatch({
    queryKey: publicSnapshotRegenerationQueryKeys.status(instanceId),
    regenerating,
    fetchStatus: () =>
      getWithToken<SnapshotRegenerationStatusResponse>(
        "/public/flow/instance/snapshot-status",
        sessionToken,
      ),
    onFinished: (status) => {
      onFinished(status);
      for (const goalId of goalIds) {
        queryClient.invalidateQueries({ queryKey: publicGoalSnapshotQueryKeys.allForGoal(goalId) });
      }
      queryClient.invalidateQueries({
        queryKey: publicSnapshotRegenerationQueryKeys.allForInstance(instanceId),
      });
      invalidateAllPortfolioSnapshots();
      // The instance-wide product-cost table reads the rebuilt compositions.
      queryClient.invalidateQueries({
        queryKey: flowInstanceQueryKeys.publicProductCosts(instanceId),
      });
      invalidateCompletion();
    },
  });
}
